import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { 
  Building, CheckCircle, ArrowRight, Phone, BarChart3, UserPlus,
  Layers, TrendingUp, Flame, Rocket, Trophy, Crown, MessageCircle,
  Users, Database, Building2, X, Check
} from 'lucide-react';
import LocationSearch from '@/components/LocationSearch';
import TurnstileWidget from '@/components/common/TurnstileWidget';

// Icon mapping
const iconMap = {
  Flame, TrendingUp, Rocket, Trophy, Crown
};

// Color configurations
const colorConfig = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-500', badge: 'bg-emerald-500', gradient: 'from-emerald-500 to-teal-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-500', badge: 'bg-blue-500', gradient: 'from-blue-500 to-indigo-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', badge: 'bg-purple-500', gradient: 'from-purple-500 to-violet-500' },
  red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-500', badge: 'bg-red-500', gradient: 'from-red-500 to-rose-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-500', badge: 'bg-amber-500', gradient: 'from-amber-500 to-orange-500' },
};

const features = [
  { icon: Layers, title: 'Inventory Management', description: 'Unit-level CRUD with real-time status tracking' },
  { icon: Users, title: 'Team Management', description: 'Role-based access for sales teams' },
  { icon: Database, title: 'Channel Partner Portal', description: 'Track leads from external brokers' },
  { icon: BarChart3, title: 'Revenue Forecasting', description: 'Predictive analytics on pipeline value' },
];

// Feature comparison data for the chart
const comparisonFeatures = [
  { name: 'Active Projects', ignite: '1', momentum: '3', accelerate: '6', dominate: '15', rule: '25+' },
  { name: 'Team Logins', ignite: '2', momentum: '5', accelerate: '10', dominate: '25', rule: 'Unlimited' },
  { name: 'Inventory Management', ignite: true, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'Email Alerts', ignite: true, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'WhatsApp Alerts', ignite: false, momentum: false, accelerate: false, dominate: true, rule: true },
  { name: 'Landing Pages', ignite: false, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'Role-Based Access', ignite: false, momentum: true, accelerate: true, dominate: true, rule: true },
  { name: 'CRM Access', ignite: false, momentum: 'Lite', accelerate: 'Full', dominate: 'Full', rule: 'Enterprise' },
  { name: 'Analytics', ignite: 'Basic', momentum: 'Basic', accelerate: 'Advanced', dominate: 'Advanced', rule: 'Enterprise' },
  { name: 'Channel Partner Module', ignite: false, momentum: false, accelerate: true, dominate: true, rule: true },
  { name: 'Tower Management', ignite: false, momentum: false, accelerate: true, dominate: true, rule: true },
  { name: 'Lead Routing', ignite: false, momentum: false, accelerate: true, dominate: true, rule: true },
  { name: 'Revenue Forecasting', ignite: false, momentum: false, accelerate: false, dominate: true, rule: true },
  { name: 'Bulk Upload (CSV/Excel)', ignite: false, momentum: false, accelerate: false, dominate: true, rule: true },
  { name: 'API/ERP Integration', ignite: false, momentum: false, accelerate: false, dominate: false, rule: true },
  { name: 'CP Portal', ignite: false, momentum: false, accelerate: false, dominate: false, rule: true },
  { name: 'Dedicated Manager', ignite: false, momentum: false, accelerate: false, dominate: true, rule: true },
];

const DeveloperPortal = () => {
  const navigate = useNavigate();
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [whatsappAddon, setWhatsappAddon] = useState({ price: 999, period: 'month' });
  const [activeBanner, setActiveBanner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    rera_number: '',
    gst_number: '',
    city: '',
    state: '',
    projects_count: '',
    message: ''
  });

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const API_URL = '';
      const [tiersRes, waAddonRes, bannerRes] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/tiers/developer`),
        fetch(`${API_URL}/api/subscription-pricing/whatsapp-addon`),
        fetch(`${API_URL}/api/coupons/banners?business_unit=partner_subscription&user_type=developer`),
      ]);
      const data = await tiersRes.json();
      const waData = await waAddonRes.json();
      const bannerData = bannerRes.ok ? await bannerRes.json() : { banners: [] };
      setTiers(data.tiers || []);
      if (waData.addon) {
        setWhatsappAddon(waData.addon);
      }
      const promos = (bannerData.banners || []).filter(
        (b) => b.discount_type === 'percentage' && b.discount_value > 0
      );
      setActiveBanner(promos[0] || null);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setTiersLoading(false);
    }
  };

  // Discounted price when a percentage promo is active (respects tenure scoping)
  const getPromoPrice = (originalPrice) => {
    if (!activeBanner || originalPrice == null) return null;
    const tenure = activeBanner.subscription_tenure;
    const cycle = billingCycle === 'yearly' ? 'annual' : billingCycle;
    if (tenure && tenure !== cycle) return null;
    return Math.round(originalPrice * (1 - activeBanner.discount_value / 100));
  };

  const getPrice = (tier) => {
    if (tier.is_enterprise) return null;
    const pricing = tier.pricing || {};
    const key = billingCycle === 'yearly' ? 'annual' : billingCycle;
    return pricing[key] ?? pricing.monthly;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Custom';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getPeriodText = () => {
    if (billingCycle === 'monthly') return '/ month';
    if (billingCycle === 'six_month') return '/ 6 months';
    return '/ year';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.company) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!captchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/leads/', {
        business_line: 'developer_registration',
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        message: formData.message,
        captcha_token: captchaToken,
        metadata: {
          company: formData.company,
          rera_number: formData.rera_number,
          gst_number: formData.gst_number,
          state: formData.state,
          projects_count: formData.projects_count
        }
      });
      
      toast.success('Registration submitted! Our enterprise team will contact you within 24-48 hours.');
      setShowRegister(false);
      setCaptchaToken(null);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'CAPTCHA_VALIDATION_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
        setCaptchaToken(null);
      } else {
        toast.error('Failed to submit registration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFeatureValue = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-emerald-400 mx-auto" />;
    if (value === false) return <span className="text-slate-600 mx-auto">—</span>;
    return <span className="text-slate-300 font-medium text-sm">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="developer-portal-page">

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/40 via-slate-950 to-slate-950" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/10 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-emerald-500/20" data-testid="developer-badge">
                <Building className="w-4 h-4" /> Developer Partner Program
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Build Smarter.<br />
                <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Sell Faster.</span>
              </h1>
              <p className="mt-6 text-slate-400 text-lg max-w-lg leading-relaxed">
                The complete PropTech platform for India's leading developers. Manage inventory, track leads, and close deals — all in one place.
              </p>
              <div className="flex gap-4 mt-8">
                <Button size="lg" className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-8 shadow-lg shadow-emerald-500/20 transition-all" onClick={() => setShowRegister(true)} data-testid="hero-register-btn">
                  Get Started Free <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 transition-all" onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20Developer%20inquiry.', '_blank')}>
                  <Phone className="w-5 h-5 mr-2" /> Schedule Demo
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-8 text-sm text-slate-500">
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> No credit card</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Free tier forever</div>
                <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> 24/7 support</div>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                {features.map((f, i) => (
                  <div key={i} className="bg-slate-900/70 backdrop-blur-xl border border-slate-800 rounded-2xl p-5 hover:-translate-y-1 transition-transform" data-testid={`feature-card-${i}`}>
                    <f.icon className="w-8 h-8 text-amber-500 mb-3" />
                    <h3 className="text-white font-semibold text-sm">{f.title}</h3>
                    <p className="text-slate-500 text-xs mt-1">{f.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 relative" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">Choose the plan that fits your scale. Upgrade anytime.</p>
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-3 mt-8" data-testid="billing-toggle">
              {['monthly', 'six_month', 'yearly'].map(cycle => (
                <button key={cycle} onClick={() => setBillingCycle(cycle)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === cycle ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                >
                  {cycle === 'monthly' ? 'Monthly' : cycle === 'six_month' ? '6 Months' : 'Yearly'}
                </button>
              ))}
            </div>
          </div>

          {tiersLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-emerald-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {tiers.map((tier, idx) => {
                const colors = colorConfig[tier.color_theme] || colorConfig.emerald;
                const price = getPrice(tier);
                const isPopular = tier.tier_key === 'accelerate';
                const IconComp = iconMap[tier.icon_name] || Building;

                return (
                  <div key={tier.tier_key || idx}
                    className={`relative flex flex-col rounded-2xl p-5 transition-all hover:-translate-y-1 ${
                      isPopular
                        ? 'bg-gradient-to-b from-emerald-900/50 to-slate-900 border-2 border-emerald-500/40 shadow-lg shadow-emerald-500/10'
                        : 'bg-slate-900/80 backdrop-blur-xl border border-slate-800 hover:border-slate-700'
                    }`}
                    data-testid={`pricing-tier-${tier.tier_key}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-lg">
                        MOST POPULAR
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-xl ${isPopular ? 'bg-emerald-500/20' : 'bg-slate-800'} flex items-center justify-center mb-3`}>
                      <IconComp className={`w-5 h-5 ${isPopular ? 'text-emerald-400' : 'text-amber-500'}`} />
                    </div>
                    <h3 className="text-white font-bold text-lg">{tier.name}</h3>
                    <p className="text-slate-500 text-xs mt-1 h-8">{tier.description || ''}</p>

                    <div className="mt-4 mb-4">
                      {tier.is_enterprise ? (
                        <span className="text-2xl font-bold text-white">Custom</span>
                      ) : (() => {
                        const promoPrice = getPromoPrice(price);
                        return (
                          <>
                            {promoPrice != null && (
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs line-through text-slate-500">{formatPrice(price)}</span>
                                <span className="text-[10px] font-semibold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">
                                  {activeBanner.discount_value}% off
                                </span>
                              </div>
                            )}
                            <span className={`text-3xl font-bold ${promoPrice != null ? 'text-amber-400' : 'text-white'}`}>
                              {formatPrice(promoPrice ?? price)}
                            </span>
                            <span className="text-slate-500 text-xs ml-1">{getPeriodText()}</span>
                            {promoPrice != null && (
                              <p className="text-[11px] text-amber-400 mt-1 font-medium" data-testid={`promo-savings-${tier.tier}`}>
                                Save {formatPrice(price - promoPrice)} with this promotion
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-2.5 mb-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Projects</span>
                        <span className="text-white font-semibold">{tier.project_limit === -1 ? 'Unlimited' : tier.is_enterprise ? '25+' : `Up to ${tier.project_limit}`}</span>
                      </div>
                    </div>

                    <div className="mt-auto">
                      {tier.is_enterprise ? (
                        <Button className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white text-sm" onClick={() => window.open('mailto:enterprise@zenstudios.in?subject=Developer Enterprise Plan Inquiry', '_blank')}>
                          <Phone className="w-4 h-4 mr-1" /> Contact Sales
                        </Button>
                      ) : (
                        <Button className={`w-full rounded-xl text-sm ${isPopular ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`} onClick={() => setShowRegister(true)}>
                          Get Started <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Compare All Features</h2>
            <p className="text-slate-400">See what's included in each developer package</p>
          </div>

          <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="text-left py-4 px-6 text-slate-400 font-semibold">Features</th>
                    <th className="text-center py-4 px-4"><div className="flex flex-col items-center"><Flame className="w-4 h-4 text-emerald-400 mb-1" /><span className="text-white text-xs">Ignite</span></div></th>
                    <th className="text-center py-4 px-4"><div className="flex flex-col items-center"><TrendingUp className="w-4 h-4 text-blue-400 mb-1" /><span className="text-white text-xs">Momentum</span></div></th>
                    <th className="text-center py-4 px-4 bg-emerald-900/30"><div className="flex flex-col items-center"><Rocket className="w-4 h-4 text-emerald-400 mb-1" /><span className="text-white text-xs">Accelerate</span><span className="text-[10px] text-emerald-400">Popular</span></div></th>
                    <th className="text-center py-4 px-4"><div className="flex flex-col items-center"><Trophy className="w-4 h-4 text-red-400 mb-1" /><span className="text-white text-xs">Dominate</span></div></th>
                    <th className="text-center py-4 px-4"><div className="flex flex-col items-center"><Crown className="w-4 h-4 text-amber-400 mb-1" /><span className="text-white text-xs">Rule</span></div></th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, idx) => (
                    <tr key={idx} className={`border-b border-slate-800/50 ${idx % 2 === 0 ? 'bg-slate-900/50' : ''}`}>
                      <td className="py-3 px-6 text-slate-300">{feature.name}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.ignite)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.momentum)}</td>
                      <td className="py-3 px-4 text-center bg-emerald-900/10">{renderFeatureValue(feature.accelerate)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.dominate)}</td>
                      <td className="py-3 px-4 text-center">{renderFeatureValue(feature.rule)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Add-on */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-slate-900 border border-emerald-500/20 rounded-2xl p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-xl text-white">WABA Integration</h3>
                <p className="text-slate-400 text-sm mt-1">Automated WhatsApp alerts for zero-hour lead response</p>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['Instant Site Inquiry', 'Auto Lead Assignment', 'Employee Escalation', 'Stale Lead Alerts'].map((f) => (
                    <div key={f} className="flex items-center gap-2 text-xs text-slate-400">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      {f}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-600 mt-3">Free with Dominate & Rule plans</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-3xl font-bold text-white">{'\u20B9'}{whatsappAddon.price?.toLocaleString('en-IN') || '999'}</div>
                <div className="text-sm text-slate-500">/ {whatsappAddon.period || 'month'}</div>
                <Button className="mt-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm" onClick={() => setShowRegister(true)}>
                  Add to Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-emerald-950/30" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-emerald-500/5 rounded-full blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Scale Your Projects?
          </h2>
          <p className="text-slate-400 mb-8 text-lg">Join leading developers across India who trust ZenStudios</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button size="lg" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-8 font-medium shadow-lg transition-all" onClick={() => setShowRegister(true)} data-testid="cta-register-btn">
              Register Now
            </Button>
            <Button size="lg" variant="outline" className="rounded-full border-slate-600 text-white hover:bg-white/5 px-8 transition-all" onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%27m%20a%20developer%20interested%20in%20your%20enterprise%20solutions.', '_blank')}>
              <Phone className="w-5 h-5 mr-2" />
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl" data-testid="registration-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Developer Registration</h2>
              <button onClick={() => setShowRegister(false)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs">Contact Person *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Phone *</Label>
                  <Input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Business Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Company Name *</Label>
                <Input value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} required className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs">RERA Registration</Label>
                  <Input value={formData.rera_number} onChange={(e) => setFormData({...formData, rera_number: e.target.value})} placeholder="Optional" className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">GST Number</Label>
                  <Input value={formData.gst_number} onChange={(e) => setFormData({...formData, gst_number: e.target.value})} placeholder="Optional" className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-600" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-xs">City *</Label>
                  <LocationSearch value={formData.city} onChange={(val) => setFormData({...formData, city: val})} placeholder="Search city..." types="city,town" required className="rounded-xl mt-1" data-testid="developer-city"
                    onLocationSelect={(location) => { if (location?.state) setFormData(prev => ({...prev, state: location.state})); }}
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">State *</Label>
                  <LocationSearch value={formData.state} onChange={(val) => setFormData({...formData, state: val})} placeholder="Search state..." types="state" required className="rounded-xl mt-1" data-testid="developer-state" />
                </div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Active/Upcoming Projects</Label>
                <Select value={formData.projects_count} onValueChange={(v) => setFormData({...formData, projects_count: v})}>
                  <SelectTrigger className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-3">1-3 Projects</SelectItem>
                    <SelectItem value="4-10">4-10 Projects</SelectItem>
                    <SelectItem value="11-25">11-25 Projects</SelectItem>
                    <SelectItem value="25+">25+ Projects</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Tell us about your company</Label>
                <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="rounded-xl mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-600" placeholder="Brief description..." />
              </div>
              <TurnstileWidget
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
                theme="dark"
                className="flex justify-center"
              />
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowRegister(false); setCaptchaToken(null); }} className="flex-1 rounded-full border-slate-700 text-slate-300 hover:bg-slate-800">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading || !captchaToken} data-testid="submit-registration-btn">
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperPortal;

